
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Calculator, Save, Bot, RefreshCw } from 'lucide-react';
import { FinancialAssumptions } from './types';
import { BalanceSheetTab } from './BalanceSheetTab';
import { ProfitLossTab } from './ProfitLossTab';
import { CashFlowTab } from './CashFlowTab';
import { BudgetTab } from './BudgetTab';
import { GeneralTab } from './GeneralTab';

export function FinancialAssumptionsForm() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  const [assumptions, setAssumptions] = useState<FinancialAssumptions>({
    balanceSheet: {
      cashProjection: 0,
      accountsReceivableGrowth: 5,
      inventoryTurnover: 6,
      accountsPayableGrowth: 3,
      debtGrowthRate: 2,
      equityProjection: 0,
    },
    profitLoss: {
      revenueGrowthRate: 10,
      costOfGoodsSoldPercentage: 65,
      operatingExpenseGrowth: 5,
      interestRate: 4,
      taxRate: 28,
      depreciationRate: 10,
    },
    cashFlow: {
      operatingCashFlowGrowth: 8,
      capitalExpenditureProjection: 0,
      workingCapitalChange: 0,
      dividendPayoutRatio: 0,
      freeCurrentFlowGrowth: 12,
    },
    budget: {
      quarterlyGrowthRate: 2.5,
      seasonalityFactor: 1.2,
      inflationRate: 3,
      contingencyReserve: 5,
      performanceTargets: 0,
    },
    general: {
      forecastPeriod: 5,
      discountRate: 8,
      baseYear: new Date().getFullYear(),
      currency: 'NZD',
      notes: '',
    },
  });

  // Helper function to validate and parse assumptions data
  const parseAssumptionsData = (data: any): FinancialAssumptions | null => {
    try {
      // Check if data has the required structure
      if (data && typeof data === 'object' && 
          data.balanceSheet && data.profitLoss && data.cashFlow && 
          data.budget && data.general) {
        return data as FinancialAssumptions;
      }
      return null;
    } catch (error) {
      console.error('Error parsing assumptions data:', error);
      return null;
    }
  };

  // Load existing assumptions when component mounts
  useEffect(() => {
    loadExistingAssumptions();
  }, [user]);

  const loadExistingAssumptions = async () => {
    if (!user) return;

    setIsLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('financial_assumptions')
        .select('assumptions_data')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading assumptions:', error);
        toast({
          title: "Info",
          description: "No existing assumptions found. Using default values.",
        });
      } else if (data && data.assumptions_data) {
        const parsedData = parseAssumptionsData(data.assumptions_data);
        if (parsedData) {
          setAssumptions(parsedData);
          toast({
            title: "Success",
            description: "Loaded existing financial assumptions.",
          });
        } else {
          toast({
            title: "Warning",
            description: "Invalid assumptions data format. Using default values.",
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      console.error('Error loading assumptions:', error);
      toast({
        title: "Error",
        description: "Failed to load existing assumptions. Using defaults.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (section: keyof FinancialAssumptions, field: string, value: string | number) => {
    setAssumptions(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: typeof value === 'string' ? value : Number(value),
      },
    }));
  };

  const generateAIAssumptions = async () => {
    if (!user) return;

    setIsGenerating(true);
    try {
      // Fetch existing financial data to inform AI suggestions
      const { data: balanceSheetData } = await supabase
        .from('balance_sheet_entries')
        .select('*')
        .eq('user_id', user.id)
        .order('period_end_date', { ascending: false })
        .limit(12);

      // Generate intelligent defaults based on available data
      const hasData = balanceSheetData && balanceSheetData.length > 0;
      
      if (hasData) {
        // Calculate some basic metrics from existing data
        const totalAssets = balanceSheetData.reduce((sum, entry) => 
          entry.account_category === 'Assets' ? sum + Number(entry.balance) : sum, 0);
        
        const totalLiabilities = balanceSheetData.reduce((sum, entry) => 
          entry.account_category === 'Liabilities' ? sum + Number(entry.balance) : sum, 0);

        // Generate more intelligent assumptions based on company size and data
        const companySize = totalAssets > 10000000 ? 'large' : totalAssets > 1000000 ? 'medium' : 'small';
        
        let updatedAssumptions = { ...assumptions };
        
        if (companySize === 'large') {
          updatedAssumptions.profitLoss.revenueGrowthRate = 8;
          updatedAssumptions.profitLoss.costOfGoodsSoldPercentage = 60;
          updatedAssumptions.cashFlow.operatingCashFlowGrowth = 10;
        } else if (companySize === 'medium') {
          updatedAssumptions.profitLoss.revenueGrowthRate = 12;
          updatedAssumptions.profitLoss.costOfGoodsSoldPercentage = 68;
          updatedAssumptions.cashFlow.operatingCashFlowGrowth = 15;
        } else {
          updatedAssumptions.profitLoss.revenueGrowthRate = 20;
          updatedAssumptions.profitLoss.costOfGoodsSoldPercentage = 70;
          updatedAssumptions.cashFlow.operatingCashFlowGrowth = 25;
        }

        setAssumptions(updatedAssumptions);
        
        toast({
          title: "AI Assumptions Generated",
          description: `Generated assumptions based on your ${companySize} company profile and historical data.`,
        });
      } else {
        // Use industry standard defaults for mining companies
        const miningDefaults = {
          ...assumptions,
          profitLoss: {
            ...assumptions.profitLoss,
            revenueGrowthRate: 15,
            costOfGoodsSoldPercentage: 65,
            operatingExpenseGrowth: 8,
            interestRate: 5,
            taxRate: 28,
            depreciationRate: 12,
          },
          cashFlow: {
            ...assumptions.cashFlow,
            operatingCashFlowGrowth: 18,
            capitalExpenditureProjection: 5000000,
            workingCapitalChange: 500000,
            freeCurrentFlowGrowth: 20,
          },
          budget: {
            ...assumptions.budget,
            quarterlyGrowthRate: 4,
            seasonalityFactor: 1.3,
            inflationRate: 3.5,
            contingencyReserve: 8,
          }
        };
        
        setAssumptions(miningDefaults);
        
        toast({
          title: "AI Assumptions Generated",
          description: "Generated mining industry standard assumptions. Add your financial data for more personalized recommendations.",
        });
      }

    } catch (error: any) {
      console.error('Error generating AI assumptions:', error);
      toast({
        title: "Error",
        description: "Failed to generate AI assumptions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const saveAssumptions = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to save assumptions.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('financial_assumptions')
        .upsert({
          user_id: user.id,
          assumptions_data: assumptions as any,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });
      
      if (error) throw error;

      toast({
        title: "Success",
        description: "Financial assumptions saved successfully.",
      });
    } catch (error: any) {
      console.error('Error saving assumptions:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save assumptions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading financial assumptions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Financial Assumptions
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={generateAIAssumptions}
                disabled={isGenerating}
                variant="outline"
                size="sm"
              >
                <Bot className="h-4 w-4 mr-2" />
                {isGenerating ? 'Generating...' : 'AI Generate'}
              </Button>
              <Button
                onClick={saveAssumptions}
                disabled={isLoading}
                size="sm"
              >
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="balance-sheet" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="balance-sheet">Balance Sheet</TabsTrigger>
              <TabsTrigger value="profit-loss">P&L</TabsTrigger>
              <TabsTrigger value="cash-flow">Cash Flow</TabsTrigger>
              <TabsTrigger value="budget">Budget</TabsTrigger>
              <TabsTrigger value="general">General</TabsTrigger>
            </TabsList>

            <TabsContent value="balance-sheet">
              <BalanceSheetTab 
                assumptions={assumptions} 
                onInputChange={handleInputChange} 
              />
            </TabsContent>

            <TabsContent value="profit-loss">
              <ProfitLossTab 
                assumptions={assumptions} 
                onInputChange={handleInputChange} 
              />
            </TabsContent>

            <TabsContent value="cash-flow">
              <CashFlowTab 
                assumptions={assumptions} 
                onInputChange={handleInputChange} 
              />
            </TabsContent>

            <TabsContent value="budget">
              <BudgetTab 
                assumptions={assumptions} 
                onInputChange={handleInputChange} 
              />
            </TabsContent>

            <TabsContent value="general">
              <GeneralTab 
                assumptions={assumptions} 
                onInputChange={handleInputChange} 
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
